import { isNull } from 'lodash/fp';
import React, { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { PlusButton } from '../../../shared/button/PlusButton';
import { Document } from '../../../shared/icons';
import { PageLoadingDots } from '../../../shared/loading-dots/PageLoadingDots';
import { Action } from '../../../shared/modal/ActionModal';
import { ArkTableColumn, ColumnSort, TableFilterType } from '../../../shared/table/types';
import { ArkTable } from '../../../shared/table/ArkTable';
import { fetchAllDocumentNamesStarted } from '../../documents/store';
import { clearMLTestDocumentsTableFilters, fetchMLTestDocumentsStarted, getIsFetchingMLTestDocuments, getMLDocumentsTableColumnSort, getMLDocumentsTableFilters, getMLDocumentsTablePageNumber, getMLDocumentsTablePageSize, getMLTestDocument, getMLTestDocuments, getMLTestDocumentsTotal, MLTestDocument, mlTestDocumentsPaginationNext, mlTestDocumentsPaginationPrevious, openMLTestDocumentStarted, setMLTestDocumentsPageSize, setMLTestDocumentsTableColumnSort, setMLTestDocumentsTableFilters, toggleMLTestDocumentsUploadModal } from './store';
import styles from './AIPlayground.module.scss';
import { AIPlaygroundDocumentUploadModal } from './AIPlaygroundDocumentUploadModal';
import { MLTestDocumentAnalysis } from './MLTestDocumentAnalysis';

export const AIPlayground: React.FC = () => {
    const dispatch = useAppDispatch();
    useFetchStarted([fetchMLTestDocumentsStarted(), fetchAllDocumentNamesStarted()]);

    const isFetchingMLTestDocuments = useAppSelector(getIsFetchingMLTestDocuments);

    const mlTestDocuments = useAppSelector(getMLTestDocuments);
    const totalDocuments = useAppSelector(getMLTestDocumentsTotal);
    const filters = useAppSelector(getMLDocumentsTableFilters);
    const pageNumber = useAppSelector(getMLDocumentsTablePageNumber);
    const columnSort = useAppSelector(getMLDocumentsTableColumnSort);
    const pageSize = useAppSelector(getMLDocumentsTablePageSize);
    const mlTestDocument = useAppSelector(getMLTestDocument);

    const nextPage = useCallback(() => dispatch(mlTestDocumentsPaginationNext()), [dispatch]);
    const previousPage = useCallback(() => dispatch(mlTestDocumentsPaginationPrevious()), [dispatch]);
    const updateFilter = useCallback((key: string, value: string | string[] | null, type: keyof TableFilterType = 'text') => dispatch(setMLTestDocumentsTableFilters(key, value, type)), [dispatch]);
    const clearAllFilters = useCallback(() => dispatch(clearMLTestDocumentsTableFilters()), [dispatch]);
    const updatePageSize = useCallback((pageSize: number) => dispatch(setMLTestDocumentsPageSize(pageSize)), [dispatch]);
    const toggleColumnSort = useCallback((columnSort: ColumnSort) => dispatch(setMLTestDocumentsTableColumnSort(columnSort)), [dispatch]);

    const openMLTestDocument = useCallback(({ mlTestDocumentId, location, mimeType }: MLTestDocument) => dispatch(openMLTestDocumentStarted(location, mlTestDocumentId, mimeType)), [dispatch]);
    const aiPlaygroundActions = useCallback((data: MLTestDocument): Action[] => [{ label: 'Select', icon: Document, onClick: () => openMLTestDocument(data) }], [openMLTestDocument]);

    const columnDefs: ArkTableColumn[] = [
        { id: 'documentDescription', header: 'Document Description', field: 'documentDescription', width: 0.48, component: 'tooltip', canFilter: true, canSort: true },
        { id: 'documentName', header: 'Document Name', field: 'documentName', width: 0.48, component: 'tooltip', canFilter: true, canSort: true },
        { id: 'actions', header: '', field: '', width: 0.04, component: 'action', actions: aiPlaygroundActions }
    ];

    const openModal = () => dispatch(toggleMLTestDocumentsUploadModal(true));

    if (isFetchingMLTestDocuments) {
        return <PageLoadingDots />;
    }

    return (
        <>
            {isNull(mlTestDocument) ?
                <div className={styles.mlTestDocumentsWrapper} data-testid='ml-test-documents-wrapper'>
                    <div className={styles.mlTestDocumentsHeaderWrapper}>
                        <div className={styles.mlTestDocumentsTitleContainer}>
                            <div className={styles.mlTestDocumentsTitle} data-testid='ml-test-documents-title'>Playground</div>
                            <PlusButton onClick={openModal} fontSize={22} testId='ml-test-documents-upload' />
                        </div>
                    </div>
                    <ArkTable
                        colDefs={columnDefs}
                        rowData={mlTestDocuments}
                        testId='system-admin-ai-manager-playground'
                        isLoading={isFetchingMLTestDocuments}
                        page={pageNumber}
                        total={totalDocuments}
                        next={nextPage}
                        previous={previousPage}
                        filters={filters}
                        updateFilter={updateFilter}
                        clearAllFilters={clearAllFilters}
                        onRowDoubleClicked={row => openMLTestDocument(row as MLTestDocument)}
                        columnSort={columnSort}
                        toggleSort={toggleColumnSort}
                        pageSize={pageSize}
                        updatePageSize={updatePageSize}
                    />
                </div>
                : <MLTestDocumentAnalysis />
            }
            <AIPlaygroundDocumentUploadModal />
        </>
    );
};
